import { useState, useMemo } from 'react';
import { useAsync } from '@hometap/htco-components';
import { getTrackDocuments } from '../documentRequests';
import { getApplicantByPersonId } from '../DocumentsUtils';

/**
 * @typedef UseTrackDocumentsReturn
 * @type {object}
 * @property {import('../documentRequests').Document[]} documents List of documents being fetched
 * @property {boolean} documentsLoading Are the documents being fetched?
 * @property {import('axios').AxiosError?} documentsError An error raise by fetching the documents
 * @property {(trackId: string) => Promise<void>} executeGetTrackDocuments Execute the fetch documents request
 * @property {function} handleFilterDocuments Function to apply filters
 * @property {object} filters Currently applied filters
 */

/** Use sorted track documents fetch request
 * @param {string} trackId Track ID
 * @param {Applicant[]} [applicants]
 * @param {object}  defaultFilters object of filter key and values
 * @returns {UseTrackDocumentsReturn}
 */
export default function useTrackDocuments(trackId, applicants = [], defaultFilters) {
  const [filters, setFilters] = useState(defaultFilters);

  const {
    results = [],
    loading: documentsLoading,
    error: documentsError,
    execute: executeGetTrackDocuments,
  } = useAsync(getTrackDocuments, {
    immediate: true,
    params: [trackId, filters],
    defaultLoading: true,
  });

  const documents = useMemo(() => {
    const records = applicants?.length ? hydrateDocumentsWithApplicantData(results, applicants) : [...results];

    records.sort(sortDocuments);

    return records;
  }, [results, applicants]);

  const handleFilterDocuments = newFilters => {
    setFilters(newFilters);
  };

  return {
    documents,
    documentsLoading,
    documentsError,
    executeGetTrackDocuments,
    handleFilterDocuments,
    filters,
  };
}

// utils

/** Sort documents by pinned status and modified date
 * @param {import('../documentRequests').Document} a First document
 * @param {import('../documentRequests').Document} b Second document
 * @returns {number} Sort order - negative if a should come before b, positive if b should come before a
 */
const sortDocuments = (a, b) => {
  // First compare pinned status (pinned docs go first)
  if (a.pinned !== b.pinned) {
    return b.pinned - a.pinned;
  }
  // Within same pinned status, sort by modified date descending
  return new Date(b.modified_at) - new Date(a.modified_at);
};

/** Hydrate documents with applicant data
 * @param {import('../documentRequests').Document[]} documents List of documents
 * @param {Applicant[]} applicants List of applicants
 * @returns {import('../documentRequests').Document[]} List of documents with applicant data
 */
const hydrateDocumentsWithApplicantData = (documents, applicants) => {
  return documents.map(document => {
    const documentWithPerson = document.contexts?.find(({ context_kind }) => context_kind === 'Person');
    if (documentWithPerson) {
      return {
        ...document,
        personId: documentWithPerson.context_id,
        applicant: getApplicantByPersonId(documentWithPerson.context_id, applicants),
      };
    }
    return document;
  });
};
